// AM Advertising Home — API client (thin wrappers over the shared api() helper).

import { api } from '../../lib/api';
import type { CurrentUser } from '../../types/user';
import type {
  AckSurface,
  AdAlertSeverity,
  AdAlertType,
  AdChannel,
  AdvertisingHomeBook,
  AdvertisingHomeCreatives,
  AdvertisingHomeOverview,
  AlertAcknowledgement,
  AlertNote,
  CreativeRankingDirection,
  GoogleShareLossReport,
  MetaLeadBreakdownReport,
  GoogleSearchTermWasteReport,
} from '../../types/advertisingHome';

export function getHomeOverview(scope: string): Promise<AdvertisingHomeOverview> {
  const p = new URLSearchParams({ scope });
  return api<AdvertisingHomeOverview>(`/api/advertising-home/overview?${p}`);
}

export function getHomeBook(
  scope: string,
  range: { from: string; to: string },
  compare: { from: string; to: string },
): Promise<AdvertisingHomeBook> {
  const p = new URLSearchParams({
    scope,
    from: range.from,
    to: range.to,
    compareFrom: compare.from,
    compareTo: compare.to,
  });
  return api<AdvertisingHomeBook>(`/api/advertising-home/book?${p}`);
}

/**
 * ⚠️ `direction` is REQUIRED — the AM gallery asks for `cheapest_cpl`, the Meta
 * specialist's waste panel for `highest_waste`. They are opposite intents over
 * the same rows, so neither caller gets to inherit the other's by default.
 */
export function getHomeCreatives(
  scope: string,
  range: { from: string; to: string },
  direction: CreativeRankingDirection,
): Promise<AdvertisingHomeCreatives> {
  const p = new URLSearchParams({ scope, from: range.from, to: range.to, direction });
  return api<AdvertisingHomeCreatives>(`/api/advertising-home/creatives?${p}`);
}


/**
 * §11d / §11e — per-account Meta lead figures on both axes.
 *
 * ⚠️ META ONLY BY CONSTRUCTION: the rows carry no `channel` and the endpoint
 * touches no Google query, so there is no Google row that could arrive with a
 * null Meta figure on it.
 */
export function getMetaLeads(
  scope: string,
  range: { from: string; to: string },
): Promise<MetaLeadBreakdownReport> {
  const p = new URLSearchParams({ scope, from: range.from, to: range.to });
  return api<MetaLeadBreakdownReport>(`/api/advertising-home/meta-leads?${p}`);
}

/** §5a — per-Location Google competitiveness numerators (the client ratios). */
export function getShareLoss(
  scope: string,
  range: { from: string; to: string },
): Promise<GoogleShareLossReport> {
  const p = new URLSearchParams({ scope, from: range.from, to: range.to });
  return api<GoogleShareLossReport>(`/api/advertising-home/share-loss?${p}`);
}

// ── Alert acknowledgements ─────────────────────────────────────────────────
// Team-wide + unscoped: the list is the same for everyone, so it is its own
// query (not folded into the overview) — ack mutations invalidate just this.

export async function listAcknowledgements(): Promise<AlertAcknowledgement[]> {
  const res = await api<{ acknowledgements: AlertAcknowledgement[] }>(
    '/api/advertising-home/acknowledgements',
  );
  return res.acknowledgements;
}

export function acknowledgeAlert(input: {
  accountId: string;
  channel: AdChannel;
  alertType: AdAlertType;
  /** ⚠️ Which board is clearing it — required, no default (see AckSurface). */
  surface: AckSurface;
  severity: AdAlertSeverity;
}): Promise<{ acknowledgement: AlertAcknowledgement }> {
  return api('/api/advertising-home/acknowledgements', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function unacknowledgeAlert(id: string): Promise<{ ok: boolean }> {
  return api(`/api/advertising-home/acknowledgements/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * The morning Slack digest opt-in (default OFF). It lives on /api/me rather
 * than under this module because it is a user preference on the user's own doc
 * — the page is just where the control sits, being the page the digest is
 * about. Returns the refreshed user so the ['me'] cache updates in place.
 */
export async function setSlackDigestEnabled(enabled: boolean): Promise<CurrentUser> {
  const res = await api<{ user: CurrentUser }>('/api/me/slack-digest', {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
  return res.user;
}

// ── Alert notes ────────────────────────────────────────────────────────────
// ⚠️ SHARED across boards and unscoped — the AM must be able to read what the
// Meta specialist wrote — so, unlike acks, a note carries NO surface. Its own
// query, invalidated by a note write and by nothing else.

export async function listAlertNotes(): Promise<AlertNote[]> {
  const res = await api<{ notes: AlertNote[] }>('/api/advertising-home/alert-notes');
  return res.notes;
}

export function createAlertNote(input: {
  accountId: string;
  channel: AdChannel;
  alertType: AdAlertType;
  body: string;
}): Promise<{ note: AlertNote }> {
  return api('/api/advertising-home/alert-notes', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * §11c — search terms that cost money and converted nothing.
 * ⚠️ SEARCH TERMS, not keywords; and we cannot see existing negatives.
 */
export function getSearchTermWaste(
  scope: string,
  range: { from: string; to: string },
): Promise<GoogleSearchTermWasteReport> {
  const p = new URLSearchParams({ scope, from: range.from, to: range.to });
  return api<GoogleSearchTermWasteReport>(`/api/advertising-home/search-term-waste?${p}`);
}
